import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '~/shadcn/ui/dialog'
import { Button } from '~/shadcn/ui/button/button'
import { Label } from '~/shadcn/ui/label'
import { Textarea } from '~/shadcn/ui/textarea'
import { Spinner } from '~/shadcn/ui/spinner'
import { FieldError } from '~/shadcn/ui/field'
import { useLocalNotifications } from '~/@mid_ui/local-notifications/useLocalNotifications'
import { getApiErrorMessage } from '~/@lib/utils/error.utils'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (message: string) => Promise<unknown>
}

export function ReportChangeDialog({ open, onOpenChange, onSubmit }: Props) {
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { setNotification } = useLocalNotifications()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)
    try {
      await onSubmit(message)
      setMessage('')
      onOpenChange(false)
      setNotification('success', 'Vaša správa bola odoslaná.')
    } catch (err) {
      setError(getApiErrorMessage(err, 'Správu sa nepodarilo odoslať.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(30rem, 600px] w-full gap-10 px-6 md:px-15 pt-12 pb-13">
        <div className="flex flex-col items-center gap-5 text-center">
          <DialogTitle className="text-2xl font-semibold">
            Nahlásiť zmenu
          </DialogTitle>
          <DialogDescription className="text-base font-medium text-primary">
            Opíšte zmenu alebo problém, ktorý chcete nahlásiť. Ozveme sa vám čo
            najskôr.
          </DialogDescription>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-10">
          <div className="flex flex-col gap-2">
            <Label
              htmlFor="report-change-message"
              className="text-sm font-medium">
              Zmena
            </Label>
            <Textarea
              id="report-change-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Text vašej správy…"
              className="min-h-32 border border-border"
            />
          </div>

          {error && <FieldError className="self-center">{error}</FieldError>}

          <Button
            type="submit"
            className="h-[45px] self-center"
            disabled={!message.trim() || isSubmitting}>
            {isSubmitting && <Spinner className="size-5" />}
            Odoslať
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
